import {
  Box,
  Grid,
  IconButton,
  Stack,
  Typography,
  Card,
  Badge,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import MDButton from "components/MDButton";
import CommonLayout from "layouts/DashboardLayout";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { openSnackbar } from "../../redux/slices/common";
import {
  closeCustomModalDialog,
  openCustomModalDialog,
  setModalLoader,
} from "../../redux/slices/modalSlice";
import {
  deleteWebCategory,
  fetchWebCategories,
  updateCategoriesOrder,
  syncBlazeCategory,
} from "../../redux/slices/category";
import EditIcon from "common/customIcons/edit";
import DeleteIcon from "common/customIcons/delete";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import SyncIcon from "@mui/icons-material/Sync";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import MenuIcon from "@mui/icons-material/Menu";
import { STILO } from "utilities/constants";
import { getData } from "utilities/common";
import CategoryTabs from "layouts/categories/categoryTabs";
import ActiveIcon from 'common/customIcons/active';
import InActiveIcon from 'common/customIcons/inactive';
import Search from "examples/Search";
import { useDebounce } from "hooks/useDebounce";
import NoDataFound from "common/component/NoDataFound";
import MainCategoryFilter from "layouts/categories/MainCategoryFilter";
import { getPermissionsByMode } from "utilities/common";
import { hasSectionPermission } from "utilities/common";
import {  HYPERWOLF } from "utilities/constants";
// const adminsMode = JSON.parse(localStorage.getItem('adminsMode'))

const getItemStyle = (isDragging, draggableStyle) => ({
  // change background colour if dragging
  background: isDragging ? "" : "",
  border: isDragging ? "none" : "",
  display: "flex",
  width: "100%",

  // styles we need to apply on draggables
  ...draggableStyle,
});
const darkIcon = {
  width: "40px !important",
  height: "40px !important",
  borderRadius: "8px !important",
  backgroundColor: "#000",
  "& img": {
    marginRight: "0 !important",
  },
};
export default function ManageCategories() {
  const dispatch = useDispatch();
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("");
  const [sortDirection, setSortDirection] = useState("");

  const navigate = useNavigate();
  const {
    isFetching = false,
    webCategoriesList = {},
    loading = false,
    isSyncing = false,
  } = useSelector((state) => state.category);
  const pageRef = useRef(0);
  const searchTermRef = useRef("");
  const mainGridApiRef = useRef("");
  const [categoriesList, setCategoriesList] = useState([]);
  const adminsMode = getData("adminsMode");

  const isStilo = JSON.parse(adminsMode) === STILO;
  const isHyperWolf = JSON.parse(adminsMode) === HYPERWOLF
  const productFiltersPayload = useRef({});
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (webCategoriesList?.values?.length) {
      let categories = [...webCategoriesList.values];

      // Apply sorting if sort parameters are set
      if (sortBy && sortDirection) {
        categories = categories.sort((a, b) => {
          let aValue, bValue;

          switch (sortBy) {
            case 'webCategoryName':
              aValue = a.webCategoryName?.toLowerCase() || '';
              bValue = b.webCategoryName?.toLowerCase() || '';
              break;
            case 'subCategoryName':
              aValue = a.adminCategories?.[0]?.categoryName?.toLowerCase() || '';
              bValue = b.adminCategories?.[0]?.categoryName?.toLowerCase() || '';
              break;
            case 'status':
              aValue = a.status?.toLowerCase() || '';
              bValue = b.status?.toLowerCase() || '';
              break;
            default:
              return 0;
          }

          if (sortDirection === 'asc') {
            return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
          } else {
            return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
          }
        });
      }
      // If no sorting is applied, categories remain in their original order

      setCategoriesList(categories);
    }
  }, [webCategoriesList?.values, sortBy, sortDirection]);

  const deleteHandler = (webCategoryId) => {
    dispatch(setModalLoader(true));
    dispatch(deleteWebCategory(webCategoryId))
      .unwrap()
      .then((res) => {
        showError(res?.message, "success");
        dispatch(closeCustomModalDialog());
        dispatch(
          fetchWebCategories({
            skip: pageRef.current,
            limit: 100,
            userType: "admin",
            searchTerm: searchTermRef.current,
          })
        );
      })
      .catch((err) => {
        dispatch(setModalLoader(false));
        showError(err?.message, "error");
      });
  };

  const showError = (message, errortype) => {
    if (errortype === "success") {
      dispatch(openSnackbar({ content: message, color: "success", icon: "check" }));
    }
    if (errortype === "error") {
      dispatch(openSnackbar({ content: message, color: "error", icon: "warning" }));
    }
  };

  const handleSyncCategories = async () => {
    try {
      const result = await dispatch(syncBlazeCategory()).unwrap();
      showError("Categories synced successfully", "success");

      // Refresh the categories list
      dispatch(
        fetchWebCategories({
          skip: pageRef.current,
          limit: 100,
          userType: "admin",
          searchTerm: searchTermRef.current,
        })
      );
    } catch (error) {
      showError(error?.message || "Failed to sync categories", "error");
    }
  };

  const onDragEnd = (result) => {
    // dropped outside the list
    if (!result.destination) {
      return;
    }

    const items = reorder(categoriesList, result.source.index, result.destination.index);
    let newOrderedCat = [...items]?.map((v, ind) => {
      return { ...v, order: ind + 1 };
    });
    setCategoriesList(newOrderedCat);
  };

  const reorder = (list, startIndex, endIndex) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);

    return result;
  };

  useEffect(() => {
    const sortParams = {};
    if (sortBy && sortDirection) {
      sortParams.sortBy = sortBy;
      sortParams.direction = sortDirection;
    }

    dispatch(
      fetchWebCategories({
        skip: pageRef.current,
        limit: 100,
        userType: "admin",
        searchTerm: searchTermRef.current,
        ...sortParams,
      })
    );
  }, [sortBy, sortDirection]);

  const saveCategoriesOrder = () => {
    dispatch(updateCategoriesOrder(categoriesList))
      .unwrap()
      .then((res) => {
        showError(res?.message, "success");
        dispatch(closeCustomModalDialog());
      })
      .catch((err) => {
        showError(err?.message, "error");
      });
  };

  useEffect(() => {
    if (mainGridApiRef?.current) {
      if (isFetching) {
        mainGridApiRef.current.showLoadingOverlay();
      } else if (webCategoriesList?.total === 0) {
        mainGridApiRef.current.showNoRowsOverlay();
      } else {
        mainGridApiRef.current.hideOverlay();
      }
    }
  }, [webCategoriesList, isFetching]);

  const searchHandler = (event) => {
    const { value } = event.target;
    setSearchTerm(value);
    searchTermRef.current = value;
    handleSearch(value);
  };

  const handleSearch = useDebounce(() => {
    pageRef.current = 0;
    searchFn();
  }, 500);

  const handleColumnSort = (columnName) => {
    let newSortBy = columnName;
    let newDirection = 'desc'; // Start with descending instead of ascending

    // If clicking the same column, cycle through: desc -> asc -> default (empty)
    if (sortBy === columnName) {
      if (sortDirection === 'desc') {
        newDirection = 'asc';
      } else if (sortDirection === 'asc') {
        // Clear sorting to go back to default state
        newSortBy = '';
        newDirection = '';
      }
    }

    setSortBy(newSortBy);
    setSortDirection(newDirection);

    // Sort the current categoriesList locally or reset to original order
    let sortedCategories = [...categoriesList];

    if (newSortBy && newDirection) {
      sortedCategories = sortedCategories.sort((a, b) => {
        let aValue, bValue;

        switch (newSortBy) {
          case 'webCategoryName':
            aValue = a.webCategoryName?.toLowerCase() || '';
            bValue = b.webCategoryName?.toLowerCase() || '';
            break;
          case 'subCategoryName':
            aValue = a.adminCategories?.[0]?.categoryName?.toLowerCase() || '';
            bValue = b.adminCategories?.[0]?.categoryName?.toLowerCase() || '';
            break;
          case 'status':
            aValue = a.status?.toLowerCase() || '';
            bValue = b.status?.toLowerCase() || '';
            break;
          default:
            return 0;
        }

        if (newDirection === 'asc') {
          return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
        } else {
          return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
        }
      });
    }

    setCategoriesList(sortedCategories);
  };

  const searchFn = (bodyPayload) => {
    const sortParams = {};
    if (sortBy && sortDirection) {
      sortParams.sortBy = sortBy;
      sortParams.direction = sortDirection;
    }

    dispatch(
      fetchWebCategories({
        skip: pageRef.current,
        limit: 100,
        userType: "admin",
        searchTerm: searchTermRef.current,
        ...sortParams,
        ...productFiltersPayload.current
      })
    ).then((res) => {
      let categories = res?.payload?.values || [];

      // Apply local sorting if sort parameters are set
      if (sortBy && sortDirection) {
        categories = [...categories].sort((a, b) => {
          let aValue, bValue;

          switch (sortBy) {
            case 'webCategoryName':
              aValue = a.webCategoryName?.toLowerCase() || '';
              bValue = b.webCategoryName?.toLowerCase() || '';
              break;
            case 'subCategoryName':
              aValue = a.adminCategories?.[0]?.categoryName?.toLowerCase() || '';
              bValue = b.adminCategories?.[0]?.categoryName?.toLowerCase() || '';
              break;
            case 'status':
              aValue = a.status?.toLowerCase() || '';
              bValue = b.status?.toLowerCase() || '';
              break;
            default:
              return 0;
          }

          if (sortDirection === 'asc') {
            return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
          } else {
            return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
          }
        });
      }
      // If no sorting is applied, categories remain in their original order

      setCategoriesList(categories);

    }).catch((err) => {
    });
  }
  const getQueryValues = (filterData) => {
    pageRef.current = 0;
    const bodyPayload = {
      categoryId: filterData?.payload?.categoryId || "",
      categoryType: filterData?.payload?.categoryTypes || "",
      maincategoryId: filterData?.payload?.maincategoryId || "",
      status: filterData?.queryPayload?.status || "",
    };
    productFiltersPayload.current = bodyPayload;

    searchFn(bodyPayload)
    setIsLoading(false);
  };

  const applyCategoryFilter = (filterData) => {
    pageRef.current = 0;
    // const bodyPayload = {
    //   maincategoryId: filterData?.payload?.maincategoryId || "",
    //   categoryId: filterData?.payload?.categoryIds || "",
    //   categoryType: filterData?.payload?.categoryTypes || "",
    //   status: filterData?.queryPayload?.status || "",
    // };
    // productFiltersPayload.current = bodyPayload;

    // searchFn(bodyPayload)

  }
  let localUser = getData('login-user-info')
  if (localUser) {
    localUser = JSON.parse(localUser)
  }

  const adminMode = localStorage.getItem("adminsMode") ? JSON.parse(localStorage.getItem("adminsMode")) : null;
  const permissionsForMode = getPermissionsByMode(localUser?.permissions, adminMode);

  const canCreate = hasSectionPermission(permissionsForMode, "Manage Website", "Create");
  const canEdit = hasSectionPermission(permissionsForMode, "Manage Website", "Edit/Update");
  const canView = hasSectionPermission(permissionsForMode, "Manage Website", "View Only");
  const canDelete = hasSectionPermission(permissionsForMode, "Manage Website", "Delete");

  return (
    <CommonLayout>
      <Box p={{ xs: 2, sm: 3, lg: 4 }} className="hemp-stilo-category">
        <CategoryTabs />
        <Grid container alignItems="center" rowSpacing={2} columnSpacing={2} mb={3}>
          <Grid item xs={12} sm={4}>
            <Typography variant="h4">Categories</Typography>
          </Grid>
          <Grid item xs={12} sm={8}>
            <Stack
              direction="row"
              justifyContent={{ xs: "start", sm: "end" }}
              flexWrap="wrap"
              alignItems="stretch"
              gap={2}
            >
              {
                adminMode===HYPERWOLF &&  <MDButton
                startIcon={<SyncIcon />}
                variant="outlined"
                color="primary"
                onClick={handleSyncCategories}
                disabled={isSyncing}
              >
                {isSyncing ? "Syncing..." : "Sync Categories"}
              </MDButton>
              }


              <Search placeholder="Search" label="" value={searchTerm} onChange={searchHandler} />
           {
            canCreate &&
                <MDButton
                  startIcon={<AddRoundedIcon fontSize="large" />}
                  variant="contained"
                  color="primary"
                  onClick={() => navigate("/categories/manageCategories/add")}
                >
                  Add Category
                </MDButton>
           }
            </Stack>
          </Grid>
        </Grid>

        <MainCategoryFilter
          applyCategoryFilter={applyCategoryFilter}
          getQueryValues={getQueryValues}
          setIsLoading={setIsLoading}
          productFiltersPayload={productFiltersPayload?.current}
        />

        {canView &&
        
        isFetching ? (
          <Box textAlign="center">
            <CircularProgress size={30} />
          </Box>
        ) : (
          <Grid container alignItems="center" rowSpacing={2} columnSpacing={2} mb={3}>
            <Grid item xs={12} lg={12} className="manage-cat-sec">
              <DragDropContext onDragEnd={onDragEnd}>
                <ul className="category-table-head">
                  <li>
                    <div
                      className="category-name"
                      style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                      onClick={() => handleColumnSort('webCategoryName')}
                    >
                      <Typography>Category Name</Typography>
                      {sortBy === 'webCategoryName' && sortDirection && (
                        <Box sx={{ ml: 1, display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                          {sortDirection === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />}
                        </Box>
                      )}
                    </div>
                    <div
                      className="sub-category-name"
                      style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                      onClick={() => handleColumnSort('subCategoryName')}
                    >
                      <Typography>Sub Category Name</Typography>
                      {sortBy === 'subCategoryName' && sortDirection && (
                        <Box sx={{ ml: 1, display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                          {sortDirection === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />}
                        </Box>
                      )}
                    </div>
                    <div
                      className="status-col"
                      style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                      onClick={() => handleColumnSort('status')}
                    >
                      <Typography>Status</Typography>
                      {sortBy === 'status' && sortDirection && (
                        <Box sx={{ ml: 1, display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                          {sortDirection === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />}
                        </Box>
                      )}
                    </div>
                    <div className="action-col">
                      <Typography>Action</Typography>
                    </div>
                  </li>
                </ul>
                <Droppable droppableId="droppable">
                  {(provided) => (
                    <ul
                      className="category-ul-list"
                      {...provided.droppableProps}
                      ref={provided.innerRef}
                    >
                      {categoriesList?.length > 0 ?
                        categoriesList?.map((item, index) => {

                          return (
                            <Draggable key={item._id} draggableId={item._id} index={index}>
                              {(provided, snapshot) => (
                                // <li
                                //   className="custon-list"
                                //   ref={provided.innerRef}
                                //   {...provided.draggableProps}
                                //   {...provided.dragHandleProps}
                                //   style={getItemStyle(
                                //     snapshot.isDragging,
                                //     provided.draggableProps.style
                                //   )}

                                // >
                                <li
                                  className="custon-list"
                                  ref={provided.innerRef} // Explicitly pass the ref
                                  {...provided.draggableProps}
                                  // draggable={provided.draggableProps.draggable} // Pass draggableProps explicitly
                                  // onDragStart={provided.dragHandleProps.onDragStart} // Handle drag events explicitly
                                  // onDragEnd={provided.dragHandleProps.onDragEnd} // Handle drag events explicitly
                                  style={getItemStyle(
                                    snapshot.isDragging,
                                    provided.draggableProps.style // Provide style directly
                                  )}
                                >
                                  <div {...provided.dragHandleProps}>
                                    <MenuIcon className="mr-2" />
                                  </div>
                                  <Box
                                    className="category-img-sec mr-2"
                                    sx={isStilo ? darkIcon : {}}
                                  >
                                    <img src={isHyperWolf ? item?.image?.url :item?.image} className="category-img" alt="" />
                                  </Box>
                                  <div className="left-section mr-2">
                                    {/* <label>Category</label> */}
                                    <Badge>{item.webCategoryName}</Badge>
                                  </div>
                                  <div className="sub-category-name-sec mr-2">
                                    {/* <label>Sub Categories</label> */}
                                    {item.adminCategories?.map((category) => {
                                      return (
                                        <Badge className="mr-2" key={category?.categoryId}>
                                          {category?.categoryName}
                                        </Badge>
                                      );
                                    })}
                                  </div>

                                  {
                                    item?.status === 'active' ?

                                      <Box color='#24CA49' display='flex' alignItems='center' className="status-col">
                                        <ActiveIcon width='18px' height='18px' flex='none' />
                                        <Typography variant='caption' fontWeight='regular' ml={.5} textTransform='capitalize'>Active</Typography>
                                      </Box>
                                      :
                                    item?.status === 'inactive' ?
                                      <Box color='#FD4438' display='flex' alignItems='center' className="status-col">
                                        <InActiveIcon width='18px' height='18px' flex='none' />
                                        <Typography variant='caption' fontWeight='regular' ml={.5} textTransform='capitalize'>Inactive</Typography>
                                      </Box>
                                      : 
                                      <Box color='#FD4438' display='flex' alignItems='center' className="status-col">
                                        <InActiveIcon width='18px' height='18px' flex='none' />
                                        <Typography variant='caption' fontWeight='regular' ml={.5} textTransform='capitalize'>Draft</Typography>
                                      </Box>
                                  }


                                  <div className="action-col">
                                    <IconButton
                                    disabled={!canEdit}
                                      onClick={() =>
                                        navigate(`/categories/manageCategories/${item._id}`)
                                      }
                                      color="primary"
                                      size="small"
                                      variant="primary"
                                    >
                                      <EditIcon width="16px" height="16px" />
                                    </IconButton>
                                    <IconButton
                                       disabled={!canDelete}
                                      onClick={() =>
                                        dispatch(
                                          openCustomModalDialog({
                                            modalData: { deleteHandler, deleteId: item?._id },
                                            modal: "DELETE_MODAL",
                                          })
                                        )
                                      }
                                      color="error"
                                      size="small"
                                      variant="primary"
                                      sx={{ marginLeft: 1 }}
                                    >
                                      <DeleteIcon width="16px" height="16px" />
                                    </IconButton>
                                  </div>
                                </li>
                              )}
                            </Draggable>
                          );
                        })
                        :
                        <div style={{ paddingBottom: "3rem" }}>

                          <NoDataFound />
                        </div>
                      }
                      {provided.placeholder}
                    </ul>
                  )}
                </Droppable>
              </DragDropContext>
              {(canCreate || canDelete )&&
                categoriesList?.length !== 0 &&
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2} mt={4} mb={2}>
                  <MDButton
                    sx={{ minWidth: "160px" }}
                    type="button"
                    variant="contained"
                    color="primary"
                    disabled={loading}
                    loading={loading}
                    onClick={saveCategoriesOrder}
                  >
                    Save
                  </MDButton>
                </Stack>
              }
            </Grid>
          </Grid>
        )}

      </Box>
    </CommonLayout>
  );
}
